/**
 * Temporary migration adapters for existing screens. They render Mantine controls and
 * preserve the existing callbacks (including typed option IDs). Do not add
 * new Semantic-style APIs here: new screens should use Mantine directly.
 */
import React, { Children, cloneElement, createContext, forwardRef, isValidElement, useContext, useId } from 'react';
import {
  Alert, Badge, Box, Button as MantineButton, Card as MantineCard, Checkbox as MantineCheckbox,
  Collapse, Divider as MantineDivider, Flex, Grid as MantineGrid, Group,
  Image as MantineImage, Loader as MantineLoader, Menu, MultiSelect, Overlay, Paper,
  Progress as MantineProgress, Select as MantineSelect, Stack, Switch, Text, Textarea,
  TextInput, Title, Tooltip, TagsInput, UnstyledButton,
} from '@mantine/core';
import { AppIcon } from '../icons/AppIcon';

export const Icon = AppIcon;
export const IconGroup = ({ children }) => <span className="tour-icon-group">{children}</span>;
const sizeOf = (value) => ({ mini: 'xs', tiny: 'xs', small: 'sm', large: 'lg', big: 'xl' }[value] || value || 'sm');
const colorOf = (color) => ({ grey: 'gray', purple: 'grape' }[color] || color);
const iconNode = (icon) => typeof icon === 'string' ? <AppIcon name={icon} /> : isValidElement(icon) ? icon : undefined;
const ButtonGroupContext = createContext({});

export const Button = forwardRef(function MigrationButton({ children, content, icon, primary, positive, negative, basic,
  color, active, toggle, compact, circular, fluid, floated, size, label, labelPosition, style, onClick,
  href, as, secondary, disabled, loading, type = 'button', ...props }, ref) {
  const group = useContext(ButtonGroupContext);
  const text = children ?? content;
  const leading = iconNode(icon);
  const iconOnly = (!text && Boolean(leading)) || (icon === true && isValidElement(text));
  const badge = label && <Label {...(typeof label === 'object' ? label : { content: label })} />;
  const labelText = typeof label === 'object' ? label?.content : label;
  const countedName = badge && ['string', 'number'].includes(typeof labelText) && typeof text === 'string'
    ? (labelPosition === 'left' ? `${labelText} ${text}` : `${text} ${labelText}`) : undefined;
  return <MantineButton ref={ref} {...props} type={href ? undefined : type} component={href ? 'a' : 'button'}
    href={disabled || loading ? undefined : href} disabled={disabled} loading={loading}
    aria-disabled={disabled || loading || undefined}
    aria-pressed={active !== undefined || toggle ? Boolean(active) : undefined}
    aria-label={props['aria-label'] || countedName || (iconOnly ? props.title || (typeof icon === 'string' ? icon : undefined) : undefined)}
    color={( ['blue', 'teal', 'green'].includes(color) ? 'cruise' : colorOf(color)) || (negative ? 'red' : positive ? 'cruise' : primary || active ? 'cruise' : 'gray')}
    variant={basic || group.basic ? 'outline' : primary || positive || negative || color || active ? 'filled' : 'light'}
    size={compact ? `compact-${sizeOf(size || group.size)}` : sizeOf(size || group.size)} radius={circular ? 'xl' : undefined}
    fullWidth={fluid} leftSection={labelPosition === 'left' && badge ? <Group gap={4}>{badge}{!iconOnly && leading}</Group> : iconOnly ? undefined : leading}
    rightSection={labelPosition === 'left' ? undefined : badge}
    onClick={(event) => { if (disabled || loading) { event.preventDefault(); return; } onClick?.(event); }}
    style={{ float: floated, ...(iconOnly ? { paddingInline: 8 } : {}), ...style }}>
    {iconOnly ? leading || text : text}
  </MantineButton>;
});
Button.Group = function MigrationButtonGroup({ size, basic, widths, fluid, children, style, ...props }) {
  return <ButtonGroupContext.Provider value={{ size, basic }}>
    <Group {...props} gap={4} grow={Boolean(widths || fluid)} wrap="wrap" style={style}>{children}</Group>
  </ButtonGroupContext.Provider>;
};

// Field context supplies a real label association to both native and Mantine
// inputs without rewriting the screens' draft-state logic.
const FieldContext = createContext({});
function Field({ children, required, error, style, ...props }) {
  const generatedId = useId();
  const elements = Children.toArray(children);
  const label = elements.find((child) => isValidElement(child) && child.type === 'label');
  const control = elements.find((child) => isValidElement(child) && ['input', 'textarea', 'select'].includes(child.type));
  const id = control?.props.id || generatedId;
  return <FieldContext.Provider value={{ id, required, error }}>
    <Box {...props} className="tour-field" style={style}>
      {elements.map((child) => {
        if (child === label) return cloneElement(child, { htmlFor: child.props.htmlFor || id, className: 'tour-field-label', children: <>{child.props.children}{required && <span aria-hidden="true"> *</span>}</> });
        if (child === control) return cloneElement(child, { id, required, 'aria-invalid': error || undefined });
        return child;
      })}
    </Box>
  </FieldContext.Provider>;
}
export function Form({ loading, children, style, onSubmit, ...props }) {
  return <Box component="form" {...props} className="tour-form" aria-busy={loading || undefined}
    onSubmit={onSubmit || ((event) => event.preventDefault())} style={{ position: 'relative', ...style }}>
    <fieldset disabled={loading} className="tour-form-fields">{children}</fieldset>
    <Dimmer active={loading}><Loader>Working…</Loader></Dimmer>
  </Box>;
}
Form.Field = Field;
Form.Group = ({ grouped, widths, children, ...props }) => grouped
  ? <Stack gap="xs" {...props}>{children}</Stack>
  : <Flex gap="sm" wrap="wrap" className="tour-form-group" {...props}>{children}</Flex>;

export const Input = forwardRef(function MigrationInput({ onChange, icon, label, labelPosition, fluid, size, ...props }, ref) {
  const field = useContext(FieldContext);
  const unit = label && typeof label === 'object' && !isValidElement(label) ? label.content : undefined;
  return <TextInput ref={ref} {...field} {...props} label={unit ? undefined : label} size={sizeOf(size)}
    leftSection={iconNode(icon)} rightSection={unit} rightSectionWidth={unit ? 36 : undefined}
    onChange={(event) => onChange?.(event, { name: props.name, value: event.currentTarget.value })} />;
});
Form.Input = Input;
Form.TextArea = function MigrationTextarea({ onChange, ...props }) {
  const field = useContext(FieldContext);
  return <Textarea {...field} {...props} onChange={(event) => onChange?.(event, { name: props.name, value: event.currentTarget.value })} />;
};
export function Checkbox({ toggle, onChange, label, ...props }) {
  const Component = toggle ? Switch : MantineCheckbox;
  // Some existing airport labels are JSX <label>s; avoid nesting native labels.
  const content = isValidElement(label) && label.type === 'label' ? label.props.children : label;
  return <Component {...props} label={content} onChange={(event) => onChange?.(event, { name: props.name, checked: event.currentTarget.checked })} />;
}
Form.Checkbox = Checkbox;

export function Select({ options = [], value, onChange, multiple, search, selection, fluid, compact,
  loading, noResultsMessage, size, clearable, allowAdditions, onAddItem, onOpen, onClose, closeOnChange, ...props }) {
  const field = useContext(FieldContext);
  // TagsInput owns creation and removal; the existing change callback receives
  // the complete list once, including any newly typed tag.
  if (allowAdditions && multiple) return <TagsInput {...field} {...props} role="combobox"
    size={sizeOf(size)} data={options.map(option => String(option.value))} value={value || []}
    clearable={clearable} splitChars={[]} acceptValueOnBlur
    aria-label={props['aria-label'] || (field.id ? undefined : props.placeholder)}
    comboboxProps={{ withinPortal: true, zIndex: 2200, onDropdownOpen: onOpen, onDropdownClose: onClose }}
    classNames={{ dropdown: 'tour-ui' }} onChange={next => onChange?.(null, { value: next })} />;
  // Mantine requires strings. Encoding the type preserves number IDs, empty
  // string (Auto), and numeric zero without confusing them with a cleared input.
  const encode = (item) => `${typeof item}:${String(item)}`;
  const byId = new Map(options.map((option) => [encode(option.value), option]));
  const data = Array.from(byId, ([id, option]) => ({ value: id, label: String(option.text ?? option.value), disabled: option.disabled }));
  const selected = (item) => byId.has(encode(item)) ? encode(item) : null;
  const Component = multiple ? MultiSelect : MantineSelect;
  return <Component {...field} {...props} role="combobox" size={sizeOf(size)} data={data}
    value={multiple ? (Array.isArray(value) ? value.map(selected).filter((item) => item != null) : []) : selected(value)}
    searchable={Boolean(search)} clearable={clearable} {...(!multiple ? { allowDeselect: false } : {})} nothingFoundMessage={noResultsMessage || 'No matching options'}
    comboboxProps={{ withinPortal: true, zIndex: 2200, onDropdownOpen: onOpen, onDropdownClose: onClose }} classNames={{ dropdown: 'tour-ui' }} rightSection={loading ? <MantineLoader size={16} /> : undefined}
    aria-busy={loading || undefined} aria-label={props['aria-label'] || (field.id ? undefined : props.placeholder)}
    onChange={(next) => onChange?.(null, { name: props.name, value: multiple ? next.map((id) => byId.get(id).value) : next == null ? '' : byId.get(next).value })}
    renderOption={({ option }) => {
      const original = byId.get(option.value);
      return <div>{original?.content || <>{iconNode(original?.icon)} {option.label}</>}
        {original?.description && <Text size="xs" c="dimmed">{original.description}</Text>}
      </div>;
    }} />;
}

export function Dropdown({ children, trigger, button, icon, floating, pointing, direction, onClick, title, basic, className, style,
  options, onOpen, onClose, scrolling, closeOnChange, ...props }) {
  if (!children && !trigger && !button) return <Select {...props} options={options} onOpen={onOpen} onClose={onClose} style={style} className={className} onClick={onClick} />;
  const target = trigger ? cloneElement(trigger, { disabled: props.disabled || trigger.props.disabled, onClick: (event) => { trigger.props.onClick?.(event); onClick?.(event); } }) : <Button icon={icon || 'bars'} aria-label={props['aria-label'] || title || 'Actions'}
    title={title} size={props.size} basic={basic} style={style} className={className} onClick={onClick} />;
  return <Menu position={direction === 'left' ? 'bottom-end' : 'bottom-start'} withinPortal shadow="md" zIndex={2200}
    onOpen={onOpen} onClose={onClose} closeOnItemClick={closeOnChange !== false}>
    <Menu.Target>{target}</Menu.Target>{children || <Menu.Dropdown className="tour-ui" style={{ maxHeight: 'min(70vh, 560px)', overflowY: 'auto' }}>
      {(options || []).map(option => option.as ? <Menu.Divider role="separator" key={option.key} /> :
        <Menu.Item key={option.key} disabled={option.disabled} leftSection={iconNode(option.icon)}
          component={option.href && !option.disabled ? 'a' : 'button'} href={option.disabled ? undefined : option.href}
          target={option.target} rel={option.target === '_blank' ? 'noopener noreferrer' : undefined}
          onClick={option.onClick}>{option.content ?? option.text}</Menu.Item>)}
    </Menu.Dropdown>}
  </Menu>;
}
Dropdown.Menu = ({ children }) => <Menu.Dropdown className="tour-ui">{children}</Menu.Dropdown>;
Dropdown.Item = ({ text, icon, children, ...props }) => <Menu.Item {...props} leftSection={iconNode(icon)}>{children ?? text}</Menu.Item>;
Dropdown.Header = ({ content, icon, ...props }) => <Menu.Label {...props}>{iconNode(icon)} {content}</Menu.Label>;
Dropdown.Divider = Menu.Divider;

export function Message({ info, negative, positive, warning, icon, content, children, onDismiss, size, className = '', ...props }) {
  return <Alert {...props} role={props.role || (negative ? 'alert' : 'note')} className={`tour-message ${className}`} color={negative ? 'red' : warning ? 'orange' : positive ? 'cruise' : info ? 'blue' : 'gray'}
    icon={iconNode(icon)} withCloseButton={Boolean(onDismiss)} onClose={onDismiss} closeButtonLabel="Dismiss message">
    {children ?? content}
  </Alert>;
}
Message.Content = ({ children }) => <Box>{children}</Box>;
Message.Header = ({ children }) => <Text component="div" fw={700} mb={4}>{children}</Text>;

export function Header({ as = 'h3', icon, children, ...props }) {
  return <Title {...props} order={Number(as.replace('h', '')) || 3} className={icon ? 'tour-heading-icon' : undefined}>{children}</Title>;
}
Header.Content = ({ children }) => <span>{children}</span>;
Header.Subheader = ({ children }) => <Text component="span" display="block" size="sm" fw={400} c="dimmed">{children}</Text>;

export function Card({ fluid, centered, children, className = '', ...props }) {
  return <MantineCard {...props} withBorder padding={0} className={`tour-card ${className}`}>{children}</MantineCard>;
}
Card.Group = ({ centered, children, className = '', ...props }) => <Flex {...props} gap="sm" wrap="wrap" justify={centered ? 'center' : undefined} className={`tour-card-group ${className}`}>{children}</Flex>;
Card.Content = ({ textAlign, extra, style, ...props }) => <Box {...props} p="md" style={{ textAlign, borderTop: extra ? '1px solid #dce5eb' : undefined, ...style }} />;
Card.Header = ({ children, floated, ...props }) => <Text {...props} component="h3" className="tour-card-title" fw={700} size="lg">{children}</Text>;
Card.Meta = ({ children, ...props }) => <Text {...props} component="div" size="sm" c="dimmed">{children}</Text>;
Card.Description = ({ children, ...props }) => <Box {...props}>{children}</Box>;

export function Label({ content, children, size, basic, circular, color, ...props }) {
  return <Badge {...props} size={sizeOf(size)} color={colorOf(color) || 'gray'} variant={basic ? 'outline' : 'light'}
    circle={circular} style={{ textTransform: 'none', height: 'auto', minHeight: 20, paddingBlock: 3, ...props.style }}>{children ?? content}</Badge>;
}
export function Segment({ basic, placeholder, textAlign, style, ...props }) {
  return <Paper {...props} withBorder={!basic} p="md" style={{ position: 'relative', textAlign, marginBlock: 12,
    ...(placeholder ? { minHeight: 120, display: 'grid', placeItems: 'center', background: '#f8fafc' } : {}), ...style }} />;
}
export const Divider = ({ horizontal, children, ...props }) => <MantineDivider {...props} my="sm" label={children} labelPosition="center" />;

export function Grid({ children, ...props }) { return <Box {...props}>{children}</Box>; }
Grid.Row = ({ centered, columns, children, style, ...props }) => <MantineGrid {...props} gutter={16} justify={centered ? 'center' : undefined}
  styles={{ inner: { width: '100%', margin: 0, height: '100%' } }} style={{ paddingBlock: 8, ...style }}>{children}</MantineGrid>;
Grid.Column = ({ width = 16, floated, ...props }) => <MantineGrid.Col {...props} span={width * 12 / 16} />;

export function Loader({ active = true, inline, content, children, size, style, ...props }) {
  if (!active) return null;
  const label = children ?? content;
  return <Stack {...props} gap={6} align="center" role="status" aria-live="polite" aria-label={label ? undefined : 'Loading'}
    style={{ display: inline === true ? 'inline-flex' : 'flex', paddingBlock: 8, ...style }}>
    <MantineLoader size={sizeOf(size)} />{label && <Text size="sm">{label}</Text>}
  </Stack>;
}
export function Dimmer({ active, inverted, children }) {
  return active ? <Overlay color={inverted ? '#fff' : '#f3f6f8'} backgroundOpacity={0.9} zIndex={10} blur={1}
    className="tour-busy-overlay" data-tour-busy="true"><div>{children}</div></Overlay> : null;
}
Dimmer.Dimmable = ({ blurring, dimmed, children, style, ...props }) => <Box {...props} aria-busy={dimmed || undefined} style={{ position: 'relative', ...style }}>{children}</Box>;

export function Progress({ percent, progress, active, color, size, ...props }) {
  const value = Math.max(0, Math.min(100, Number(percent) || 0));
  return <Box {...props}><MantineProgress value={value} color={colorOf(color)} size={sizeOf(size)} aria-label="Progress" />
    {progress && <Text size="xs" ta="center">{Math.round(value)}%</Text>}</Box>;
}

export function Popup({ trigger, content, position = 'top center' }) {
  const placement = { 'right center': 'right', 'left center': 'left', 'bottom center': 'bottom' }[position] || 'top';
  return <Tooltip label={content} position={placement} withArrow multiline maw={280}
    color="white" c="dark" className="tour-ui" zIndex={2300} events={{ hover: true, focus: true, touch: false }}>
    {trigger}
  </Tooltip>;
}
export const Image = forwardRef(function MigrationImage({ floated, spaced, size, style, alt = '', ...props }, ref) {
  return <MantineImage ref={ref} {...props} alt={alt} w={size === 'mini' ? 35 : undefined}
    style={{ float: floated, marginInline: spaced ? 4 : undefined, ...style }} />;
});
export function List({ bulleted, divided, relaxed, selection, size, style, children, ...props }) {
  return <Box component="ul" {...props} className={`tour-list ${divided ? 'tour-list-divided' : ''}`}
    style={{ listStyle: bulleted ? 'disc' : 'none', paddingLeft: bulleted ? 20 : 0, ...style }}>{children}</Box>;
}
List.Item = ({ children, ...props }) => <Box component="li" {...props}>{children}</Box>;
List.Content = ({ floated, style, ...props }) => <Box {...props} style={{ float: floated, ...style }} />;
List.Header = ({ children }) => <Text fw={700}>{children}</Text>;
List.Description = ({ children }) => <Box>{children}</Box>;

export const Accordion = ({ styled, fluid, ...props }) => <Paper withBorder {...props} />;
Accordion.Title = ({ active, children, ...props }) => <UnstyledButton {...props} className="tour-accordion-title" aria-expanded={active}>{children}</UnstyledButton>;
Accordion.Content = ({ active, children }) => <Collapse in={active}><Box p="sm">{children}</Box></Collapse>;
